import React from 'react';
import { connect } from 'react-redux';

import { StoreProps } from 'store';
import { AdvancedAnalyticsEntityType } from './components/AdvancedAnalyticsEntityTypes/AdvancedAnalyticsEntityTypes';
import {
  ChartViewData,
  IDictionary,
  IFormValidateHandler,
  IFormValues,
  IOnChange,
  IValidateHandler,
  IValidator,
} from 'types/Analytics';

import { createChart } from 'actions/analytics';
import { closeModal } from 'actions/modal';

import { GraphDetails } from './GraphDetails';
import { ServiceGetMockedOptionsByType } from './services/ServiceGetMockedOptionsByType';
import { ServiceSettingsFormHelpers } from './services/ServiceSettingsFormHelpers';
import { types } from './services/ServiceGetModalChartTypes';
import DateHelpers from 'helpers/Date';

import { AnyObject } from 'types/Common';

import DateFormats from 'constants/dateFormats';
import { FormValidate } from './components/settingsForm/InOutFormConfig';

const WEEK = 7;
const MAX_INOUT_TABLE_AMOUNT = 3;
const FORM_VALIDATORS = {
  [AdvancedAnalyticsEntityType.inout]: FormValidate,
};

interface OwnProps {
  operationType: IDictionary;
  callback: (savingData: AnyObject) => void;
}

interface ConnectedProps {
  projects: { list: IDictionary; isLoading: boolean };
  country: { list: IDictionary; isLoading: boolean };
  paymentMethod: { list: IDictionary; isLoading: boolean };
  paymentMethodGroup: { list: IDictionary; isLoading: boolean };
  currency: { list: IDictionary; isLoading: boolean };
  analyticsBrand: { list: IDictionary; isLoading: boolean };
  successRateTransactionType: { list: IDictionary; isLoading: boolean };
  inOutTableAmount: number;
  declineReasonsTableAmount: number;
  vatState: { list: IDictionary; isLoading: boolean };
}

type Props = OwnProps & ConnectedProps & StoreProps;

interface State {
  graphType: AdvancedAnalyticsEntityType;
  values: { [key: string]: IFormValues };
  options: ChartViewData | null;
  validationErrors:
    | {
        [key: string]: string;
      }
    | {};
  isLoading: boolean;
  formValidationErrors:
    | {
        [key: string]: string;
      }
    | {};
  pristineFields: { [key: string]: boolean };
  initialValues: IFormValues;
}

class GraphDetailsContainer extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.setGraphType = this.setGraphType.bind(this);
    this.submitButtonDisabledConditions =
      this.submitButtonDisabledConditions.bind(this);

    this.state = {
      ...this.initializeState({
        graphType: AdvancedAnalyticsEntityType.operations,
      }),
    } as State;
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
    const { graphType } = this.state;
    const prevValues = prevState.values[AdvancedAnalyticsEntityType[graphType]];
    const currentValues =
      this.state.values[AdvancedAnalyticsEntityType[graphType]];

    const prevChartType =
      prevValues !== undefined && 'chartType' in prevValues
        ? prevValues.chartType
        : null;
    const chartType =
      prevValues !== undefined && 'chartType' in currentValues
        ? currentValues.chartType
        : null;

    const supportedGraphTypes = [
      AdvancedAnalyticsEntityType.operations,
      AdvancedAnalyticsEntityType.topten,
      AdvancedAnalyticsEntityType.fraud_report,
      AdvancedAnalyticsEntityType.chargeback,
      AdvancedAnalyticsEntityType.success_rate,
      AdvancedAnalyticsEntityType.vat_analytics,
    ];

    if (
      supportedGraphTypes.includes(graphType) &&
      chartType &&
      prevChartType &&
      chartType !== prevChartType
    ) {
      const options = ServiceGetMockedOptionsByType({
        type: chartType,
        group: graphType,
      });

      this.setState({
        options,
      });
    }
  }

  submitButtonDisabledConditions() {
    const { inOutTableAmount, declineReasonsTableAmount } = this.props;
    const { graphType } = this.state;
    const acc: Array<{ tooltipText: string; disabled: boolean }> = [];

    if (
      inOutTableAmount >= MAX_INOUT_TABLE_AMOUNT &&
      graphType === AdvancedAnalyticsEntityType.inout
    ) {
      acc.push({
        tooltipText: 'analytics.editForm.addInOutChart.toolTip',
        disabled: true,
      });
    } else if (
      declineReasonsTableAmount >= MAX_INOUT_TABLE_AMOUNT &&
      graphType === AdvancedAnalyticsEntityType.declineReasons
    ) {
      acc.push({
        tooltipText: 'analytics.editForm.addPaymentsPivot.toolTip',
        disabled: true,
      });
    }

    return acc;
  }

  get dictionariesLoading() {
    const {
      projects,
      currency,
      paymentMethod,
      paymentMethodGroup,
      country,
      analyticsBrand,
      successRateTransactionType,
      vatState,
    } = this.props;

    return (
      projects.isLoading &&
      currency.isLoading &&
      paymentMethod.isLoading &&
      paymentMethodGroup.isLoading &&
      country.isLoading &&
      analyticsBrand.isLoading &&
      successRateTransactionType.isLoading &&
      vatState.isLoading
    );
  }

  render() {
    const {
      projects,
      currency,
      paymentMethod,
      paymentMethodGroup,
      country,
      analyticsBrand,
      successRateTransactionType,
      dispatch,
      vatState,
    } = this.props;
    const {
      graphType,
      validationErrors,
      options,
      values,
      isLoading,
      formValidationErrors,
      pristineFields,
    } = this.state;

    const formValidators =
      FORM_VALIDATORS[graphType] === undefined
        ? []
        : FORM_VALIDATORS[graphType];
    const formValidate = formValidators.reduce((acc, { fields }) => {
      acc = [...acc, ...fields];

      return acc;
    }, []);

    return (
      <GraphDetails
        titleToken='analytics.editForm.addChart.label'
        chartTypes={types}
        isLoading={isLoading || this.dictionariesLoading}
        validationErrors={validationErrors}
        options={options}
        values={values[AdvancedAnalyticsEntityType[graphType]]}
        multiSelectItems={{
          projects: projects.list,
          currency: currency.list,
          paymentMethod: paymentMethod.list,
          country: country.list,
          analyticsBrand: analyticsBrand.list,
          paymentMethodGroup: paymentMethodGroup.list,
          successRateTransactionType: successRateTransactionType.list,
          vatState: vatState.list,
        }}
        onChange={this.onChange}
        selectedType={graphType}
        setType={this.setGraphType}
        closeModal={this.closeModal}
        saveChanges={this.saveChanges}
        validateHandler={this.validateHandler}
        formValidationErrors={formValidationErrors}
        submitButtonDisabledConditions={this.submitButtonDisabledConditions()}
        pristineFields={pristineFields}
        formValidatedFields={formValidate}
        dispatch={dispatch}
      />
    );
  }

  generatePristineFieldMap(values) {
    return Object.fromEntries(
      Object.entries(values).map(([key]) => [key, true])
    );
  }

  getInitialValidationResults(
    validationMap: { [key: string]: IValidator[] },
    initialValues: IFormValues
  ) {
    return Object.entries(validationMap).reduce(
      (acc, [field, validatorList]) => {
        acc[field] = validatorList.map((validator) =>
          validator(initialValues[field])
        );

        return acc;
      },
      {}
    );
  }

  initializeState({ graphType }: { graphType: AdvancedAnalyticsEntityType }) {
    const today = DateHelpers.getEndDay(DateHelpers.getDate()).format(
      DateFormats.datetime
    );
    const weekEarlier = DateHelpers.getStartDay(
      DateHelpers.subtractDays(WEEK)
    ).format(DateFormats.datetime);

    if (graphType === AdvancedAnalyticsEntityType.operations) {
      const initialValues = {
        chart: AdvancedAnalyticsEntityType.operations,
        chartName: '',
        groupByDate: '1d',
        groupBy: 'operationType',
        chartType: 'line',
        chartData: 'count',
        operationStatus: [],
        period: { from: weekEarlier, to: today },
        projects: [],
      };

      return {
        isLoading: false,
        graphType: AdvancedAnalyticsEntityType.operations,
        values: {
          [AdvancedAnalyticsEntityType[graphType]]: initialValues,
        },
        validationErrors: {},
        formValidationErrors: {},
        options: ServiceGetMockedOptionsByType({
          type: 'line',
          group: graphType,
        }),
        pristineFields: this.generatePristineFieldMap(initialValues),
        initialValues,
      };
    } else if (graphType === AdvancedAnalyticsEntityType.inout) {
      const initialValues = {
        chart: AdvancedAnalyticsEntityType.inout,
        chartName: '',
        chartData: 'amount',
        groupByDate: '1d',
        groupByRows: [],
        groupByColumns: [],
        projects: [],
        paymentMethod: [],
        channelCurrency: [],
        type: [],
        period: {
          from: weekEarlier,
          to: today,
        },
        country: [],
      };

      return {
        isLoading: false,
        graphType: AdvancedAnalyticsEntityType.inout,
        values: {
          [AdvancedAnalyticsEntityType[graphType]]: initialValues,
        },
        validationErrors: {},
        formValidationErrors: {},
        options: ServiceGetMockedOptionsByType({
          type: 'table',
          group: graphType,
        }),
        pristineFields: this.generatePristineFieldMap(initialValues),
        initialValues,
      };
    } else if (graphType === AdvancedAnalyticsEntityType.declineReasons) {
      const initialValues = {
        chartName: '',
        chartData: 'count',
        groupByRows: [],
        groupByColumns: [],
        groupByDate: '1d',
        period: {
          from: weekEarlier,
          to: today,
        },
        projectId: [],
        operationType: [],
        operationStatus: [],
        countryByBin: [],
        countryByIp: [],
        paymentMethod: [],
        currency: [],
      };

      return {
        isLoading: false,
        graphType: AdvancedAnalyticsEntityType.declineReasons,
        values: {
          [AdvancedAnalyticsEntityType[graphType]]: initialValues,
        },
        validationErrors: {},
        formValidationErrors: {},
        options: ServiceGetMockedOptionsByType({
          type: 'table',
          group: graphType,
        }),
        pristineFields: this.generatePristineFieldMap(initialValues),
        initialValues,
      };
    } else if (graphType === AdvancedAnalyticsEntityType.topten) {
      return {
        isLoading: false,
        graphType: AdvancedAnalyticsEntityType.topten,
        values: {
          [AdvancedAnalyticsEntityType[graphType]]: {
            chart: AdvancedAnalyticsEntityType.topten,
            chartName: '',
            chartData: 'count',
            dimension: 'paymentMethod',
            chartType: 'bar',
            period: { from: weekEarlier, to: today },
            projects: [],
          },
        },
        options: ServiceGetMockedOptionsByType({
          type: 'bar',
          group: graphType,
        }),
      };
    } else if (graphType === AdvancedAnalyticsEntityType.fraud_report) {
      return {
        isLoading: false,
        graphType: AdvancedAnalyticsEntityType.fraud_report,
        values: {
          [AdvancedAnalyticsEntityType[graphType]]: {
            chart: AdvancedAnalyticsEntityType.fraud_report,
            chartName: '',
            chartData: 'numberOfFraudOperations',
            chartType: 'simple_table',
            period: { from: weekEarlier, to: today },
            projectId: [],
            groupByMultiSelect: [],
            groupBy: 'cardType',
            brandId: [],
            groupByDate: '',
            issuerCountry: [],
            cardType: [],
          },
        },
        options: ServiceGetMockedOptionsByType({
          type: 'simple_table',
          group: graphType,
        }),
      };
    } else if (graphType === AdvancedAnalyticsEntityType.chargeback) {
      return {
        isLoading: false,
        graphType: AdvancedAnalyticsEntityType.chargeback,
        values: {
          [AdvancedAnalyticsEntityType[graphType]]: {
            chart: AdvancedAnalyticsEntityType.chargeback,
            chartName: '',
            chartData: 'count_chargeback_id',
            chartType: 'bar',
            period: { from: weekEarlier, to: today },
            projects: [],
            groupByMultiSelect: [],
            groupBy: 'cardType',
          },
        },
        options: ServiceGetMockedOptionsByType({
          type: 'bar',
          group: graphType,
        }),
      };
    } else if (graphType === AdvancedAnalyticsEntityType.success_rate) {
      return {
        isLoading: false,
        graphType,
        values: {
          [AdvancedAnalyticsEntityType[graphType]]: {
            chart: graphType,
            chartName: '',
            chartData: 'count',
            chartType: 'simple_table',
            groupByDate: '1d',
            period: { from: weekEarlier, to: today },
            currency: [],
            transactionType: [],
            paymentMethodGroup: [],
            paymentMethodType: [],
            issuerBankCountryName: [],
            projectId: [],
          },
        },
        options: ServiceGetMockedOptionsByType({
          type: 'simple_table',
          group: graphType,
        }),
      };
    } else if (graphType === AdvancedAnalyticsEntityType.vat_analytics) {
      return {
        isLoading: false,
        graphType,
        values: {
          [AdvancedAnalyticsEntityType[graphType]]: {
            chart: graphType,
            chartName: '',
            chartData: 'count',
            chartType: 'simple_table',
            taxOverview: 'country',
            groupByDate: '',
            period: { from: weekEarlier, to: today },
            paymentMethodType: [],
            vatCountry: [],
            vatState: [],
          },
        },
        options: ServiceGetMockedOptionsByType({
          type: 'simple_table',
          group: graphType,
        }),
      };
    }
  }

  validateHandler: IValidateHandler = (name, value, formValues, validator) => {
    const { validationErrors } = this.state;
    const validationResult = validator
      .map((func) => func(name))
      .filter((err) => err !== undefined);

    if (validationResult.length !== 0) {
      const newErrors = { [name]: validationResult };

      this.setState({
        validationErrors: { ...validationErrors, ...newErrors },
      });
    } else {
      const withoutCurrentFieldErrors = Object.entries(validationErrors).reduce(
        (acc, [field, err]) => {
          if (field === name) {
            return acc;
          }

          acc[field] = err;

          return acc;
        },
        {}
      );

      this.setState({
        validationErrors: withoutCurrentFieldErrors,
      });
    }
  };

  formValidateHandler: IFormValidateHandler = (name, value, formValues) => {
    const { formValidationErrors, graphType } = this.state;
    const formValidate = FORM_VALIDATORS[graphType];

    if (formValidate !== undefined) {
      const newErrors = formValidate.reduce((acc, { _, errId, validator }) => {
        const validationResult = validator(name, value, formValues);

        acc[errId] = validationResult;

        return acc;
      }, {});

      this.setState({
        formValidationErrors: { ...formValidationErrors, ...newErrors },
      });
    }
  };

  onChange: IOnChange = (name, value, onChangeCallback) => {
    const { graphType } = this.state;
    const values = this.state.values[AdvancedAnalyticsEntityType[graphType]];
    const newValues = { ...values, [name]: value };
    const cbHandledValues =
      onChangeCallback !== undefined
        ? { ...onChangeCallback(newValues, values) }
        : newValues;

    ServiceSettingsFormHelpers.adjustOptionalFieldsValues({
      values: cbHandledValues,
      chartType: graphType,
    });

    this.setState({
      values: {
        [AdvancedAnalyticsEntityType[graphType]]: { ...cbHandledValues },
      },
    });
  };

  setGraphType(graphType: AdvancedAnalyticsEntityType) {
    const newState = { ...this.initializeState({ graphType }) } as State;

    this.setState({ ...newState });
  }

  saveChanges = async () => {
    const { dispatch } = this.props;
    const { graphType } = this.state;
    const values = this.state.values[AdvancedAnalyticsEntityType[graphType]];

    this.setState({ isLoading: true });

    await dispatch(createChart({ graphType, values }));

    this.setState({ isLoading: false });
  };

  closeModal = () => {
    const { dispatch } = this.props;
    dispatch(closeModal());
  };
}

const mapStateToProps = (state): ConnectedProps => {
  const {
    filtersValues,
    analytics: {
      chartData: { byId },
    },
  }: {
    filtersValues?: any;
    analytics: { chartData: { byId: { [id: string]: any } } };
  } = state;

  const inOutTableAmount = Object.entries(byId).filter(
    ([_, item]) => item.chart === AdvancedAnalyticsEntityType.inout
  ).length;

  const declineReasonsTableAmount = Object.entries(byId).filter(
    ([_, item]) =>
      item.chartGroup === AdvancedAnalyticsEntityType.declineReasons
  ).length;

  return {
    projects: filtersValues?.enabledProject ?? {},
    country: filtersValues?.country ?? {},
    paymentMethod: filtersValues?.paymentMethodType ?? {},
    paymentMethodGroup: filtersValues?.paymentMethodGroup ?? {},
    currency: filtersValues?.currency ?? {},
    analyticsBrand: filtersValues?.analyticsBrand ?? {},
    successRateTransactionType: filtersValues?.successRateTransactionType ?? {},
    inOutTableAmount,
    declineReasonsTableAmount,
    vatState: filtersValues?.vatState ?? {},
  };
};

const GraphDetailsContainer_ = connect(mapStateToProps)(GraphDetailsContainer);

export { GraphDetailsContainer_ as GraphDetailsContainer };
